import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import { makeStyles } from '@mui/styles'
import { Box, Button, ButtonGroup, FormControlLabel, Grid, Paper, Stack, Theme, Typography } from '@mui/material'
import AccessTime from '@mui/icons-material/AccessTime'
import Facebook from '@mui/icons-material/Facebook'
import Instagram from '@mui/icons-material/Instagram'
import LinkedIn from '@mui/icons-material/LinkedIn'
import Google from '@mui/icons-material/Google'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

import TooltipComponent from '@professocial/professocialFormComponents/FormComponents/TooltipComponent'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import SwitchComponent from '@professocial/professocialFormComponents/FormComponents/SwitchComponent'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import DropDownComponent, {
  DropDownElement
} from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import {
  GeneralPostingOptions,
  PostingTemplate,
  SocialMediaNetwork
} from '@professocial/professocialTypes/types/commonTypes'

import { DeferredPostStatus, TopicsWithMetadataResponse, TopicWithMetadata } from '@3m5/sm_publish-frontend-api'

import deLocale from 'date-fns/locale/de'
import { startOfToday } from 'date-fns'

import { getGeneralPostingOptions, getUser } from '../../../store/common/common.selectors'
import validatePosting from '../../../util/validatePosting'
import { PostingError, PostingType, PostPlaning, Restrictions } from '../../types/types'
import { getAboTopics, getPostingType, getUpdateTemplateId } from '../../../store/posting/posting.selector'
import { PostingActionType } from '../../../screens/ProfessocialPosting'
import countAllowedNetworks from '../../../util/countAllowedNetworks'
import formatDate from '@professocial/professocialFormComponents/FormComponents/util/formatDate'
import getDateFormat from '../../../defaults/getDateFormat'
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers'
import { UserSettings } from '@professocialApi/professocial-core-frontend-api/models/UserSettings'
import { setPostingType } from '../../../store/posting/posting.actions'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: '100%',
  },
  paperButtons: {
    marginTop: '10px',
    padding: '19px 22px',
  },
  optionsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  options: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1.1),
    '& .MuiSvgIcon-root': {
      verticalAlign: 'middle',
    },
  },
  innerOptions: {
    marginRight: theme.spacing(5),
  },
  buttonGroup: {
    float: 'right',
    marginTop: theme.spacing(1),
    height: theme.spacing(5),
    '& .MuiButton-disableElevation:hover': {
      color: '#fff',
    },
  },
  dateOptions: {
    display: 'flex',
    justifyContent: 'end',
  },
}))

interface IProps {
  allowedSocialMediaNetworks?: SocialMediaNetwork
  posting: PostingTemplate
  restrictions?: Restrictions
  date: PostPlaning
  changePosting?: (post: PostingTemplate) => void
  changePublishDate?: (span: PostPlaning) => void
  sendAction?: (action: PostingActionType) => void
}

const CreatePostingOptions: React.FC<IProps> = (props) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()

  const user: UserSettings | undefined = useSelector(getUser)
  const storedAboTopics: TopicsWithMetadataResponse | undefined = useSelector(getAboTopics)
  const templateID: string | undefined = useSelector(getUpdateTemplateId)
  const generalPostingOptions: GeneralPostingOptions = useSelector(getGeneralPostingOptions)
  const postingType: PostingType = useSelector(getPostingType)

  const [posting, setPosting] = useState<PostingTemplate>(props.posting)
  const [socialMediaNetworks, setSocialMediaNetworks] = useState<SocialMediaNetwork>(props.allowedSocialMediaNetworks || {})
  const [socialMediaNetworkCount, setSocialMediaNetworkCount] = useState<number>(countAllowedNetworks(socialMediaNetworks))

  const dispatchPostingType = useCallback((params: PostingType) => dispatch(setPostingType(params)), [dispatch])

  useEffect(() => {
    setPosting(props.posting)
    setTopicId(props.posting.aboTopicId)
    setAddHashtag(props.posting.addHashtag || false)
    setManipulateLinks(props.posting.manipulateLinks || false)
    setFacebook(props.posting.socialMediaNetwork.facebook || false)
    setInstagram(props.posting.socialMediaNetwork.instagram || false)
    setLinkedIn(props.posting.socialMediaNetwork.linkedIn || false)
    setGoogleMyBusiness(props.posting.socialMediaNetwork.googleMyBusiness || false)
    props.date.start || props.date.end ? setShow(true) : setShow(false)
  }, [props.posting],
  )

  useEffect(() => {
    setSocialMediaNetworks(props.allowedSocialMediaNetworks || {})
    setSocialMediaNetworkCount(countAllowedNetworks(props.allowedSocialMediaNetworks))
    if (props.allowedSocialMediaNetworks && countAllowedNetworks(props.allowedSocialMediaNetworks) === 1) {
      const post = {
        ...props.posting,
        socialMediaNetwork: props.allowedSocialMediaNetworks,
      }
      if (props.allowedSocialMediaNetworks.facebook) {
        setFacebook(true)
      }
      if (props.allowedSocialMediaNetworks.instagram) {
        setInstagram(true)
      }
      if (props.allowedSocialMediaNetworks.linkedIn) {
        setLinkedIn(true)
      }
      if (props.allowedSocialMediaNetworks.googleMyBusiness) {
        setGoogleMyBusiness(true)
      }
      setPosting(post)
      props.changePosting && props.changePosting(post)
    }
  }, [props.allowedSocialMediaNetworks],
  )

  const initialPostingOptions = [
    { key: PostingType.template, value: t('postingCommon:templatePosting') },
    { key: PostingType.posting, value: t('postingCommon:topicPosting') },
  ]

  const [postingOptions, setPostingOptions] = useState<DropDownElement[]>(initialPostingOptions)
  const [topicData, setTopicData] = useState<DropDownElement[]>([])
  const [categoryData, setCategoryData] = useState<DropDownElement[]>([])
  const [postingErrors, setPostingErrors] = useState<PostingError[]>([])
  const [buttonTooltip, setButtonTooltip] = useState<string>('')
  const [topicId, setTopicId] = useState<string | undefined>(props.posting.aboTopicId)
  const [manipulateLinks, setManipulateLinks] = useState<boolean>(props.posting.manipulateLinks || false)
  const [addHashtag, setAddHashtag] = useState<boolean>(props.posting.addHashtag || false)
  const [facebook, setFacebook] = useState<boolean>(props.posting.socialMediaNetwork.facebook || false)
  const [instagram, setInstagram] = useState<boolean>(props.posting.socialMediaNetwork.instagram || false)
  const [linkedIn, setLinkedIn] = useState<boolean>(props.posting.socialMediaNetwork.linkedIn || false)
  const [googleMyBusiness, setGoogleMyBusiness] = useState<boolean>(props.posting.socialMediaNetwork.googleMyBusiness || false)
  const [show, setShow] = useState<boolean>()
  const [showTopicError, setShowTopicError] = useState<boolean>(false)

  const showAsPopUp = generalPostingOptions.showAsPopUp || false

  const [disablePostingButton, setDisablePostingButton] = useState<boolean>(!validatePosting(posting, props.restrictions, generalPostingOptions))

  const handleChange = (newValue: Date | null, point: string) => {
    props.changePublishDate && props.changePublishDate({ ...props.date, [point]: newValue })
  }

  const changeSocialMedia = (checked: boolean, socialMedia: string) => {
    const socialMediaNetwork = { ...posting.socialMediaNetwork, [socialMedia]: checked }
    const post = {
      ...posting,
      socialMediaNetwork,
    }

    props.changePosting && props.changePosting(post)
  }

  const changeTopic = (option: DropDownElement | undefined) => {
    const post: PostingTemplate = {
      ...posting,
      aboTopicId: option ? option.key : undefined,
    }
    setPosting(post)
    setTopicId(option ? option.key : undefined)
    props.changePosting && props.changePosting(post)
  }

  const setCategoryDropdowns = () => {
    if (storedAboTopics) {
      setPostingOptions(initialPostingOptions)
      const topics: DropDownElement[] = []
      const categories: DropDownElement[] = []
      storedAboTopics && storedAboTopics.topics.map((elem: TopicWithMetadata) => {
        if (elem.topic.active) {
          if (elem.topic.manualPostingAllowed || elem.topic.automaticPostingAllowed) {
            elem.topic.id && topics.push({
              key: elem.topic.id,
              value: elem.topic.name,
            })
          } else {
            elem.topic.id && categories.push({
              key: elem.topic.id,
              value: elem.topic.name,
            })
          }
        }
      })

      if (topics.length === 0) {
        setPostingOptions([initialPostingOptions[0]])
      }
      if (categories.length === 0) {
        setPostingOptions([initialPostingOptions[1]])
      }
      if (topics.length === 0 && categories.length === 0) {
        setPostingOptions([])
        setShowTopicError(true)
      }
      setTopicData(topics)
      setCategoryData(categories)
    }
  }

  const validatePost = async () => {
    if (posting) {
      const errors = await validatePosting(posting, props.restrictions, generalPostingOptions)
      setPostingErrors(errors)
    }
  }

  const handlePublishAction = () => {
    if (generalPostingOptions.allowTemplateEdit && !templateID) {
      props.sendAction && props.sendAction(PostingActionType.CREATE_TEMPLATE)
    } else if (generalPostingOptions.allowTemplateEdit && templateID) {
      props.sendAction && props.sendAction(PostingActionType.UPDATE_TEMPLATE)
      // noch einen else if statement, wenn templateID da ist, und allowTemplateEdit ist gleich false
    } else {
      if (props.posting.pagePostId || (props.posting.deferredPostId && props.posting.status !== DeferredPostStatus.AwaitingUserConfirmation)) {
        props.sendAction && props.sendAction(PostingActionType.CHANGE_POSTING)
      } else {
        props.sendAction && props.sendAction(PostingActionType.CREATE_POSTING)
      }
    }
  }

  /**
   *  bei templates create > Vorlage anlegen
   *  bei templates edit > Vorlage anpassen
   *  bei posting create > Beitrag am xx.xx.xxxx veröffentlichen (Beitrag jetzt veröffentlichen, wenn keine Zeit eingestellt ist)
   *  bei posting edit > Beitrag anpassen
   */
  const setButtonText = () => {
    return templateID
      ? (user?.role === 'EDITOR' ? t('postingCommon:updateEditor') : t('postingCommon:updateUser'))
      : (user?.role === 'EDITOR' && postingType === PostingType.template ? t('postingCommon:createEditor')
        : ((props.posting.deferredPostId && props.posting.status !== DeferredPostStatus.AwaitingUserConfirmation) || props.posting.pagePostId)
          ? t('postingCommon:updateUser')
          : (props.date.start
            ? t('postingCommon:createUserTime', { date: formatDate(setMinDate(new Date(props.date.start)).getTime(), getDateFormat()) })
            : t('postingCommon:createUser')))
  }

  // date shouldn't be in the past
  const setMinDate = (date?: Date) => date && date > new Date() ? date : new Date()


  useEffect(() => {
    if (postingErrors.length > 0) {
      setDisablePostingButton(true)
      setButtonTooltip(t(`postingCommon:${postingErrors[0].errorCode}`))
    } else {
      setDisablePostingButton(false)
      setButtonTooltip('')
    }
  }, [postingErrors],
  )

  useEffect(() => {
    setCategoryDropdowns()
  }, [storedAboTopics],
  )

  useEffect(() => {
    validatePost()
  }, [posting, posting.socialMediaNetwork, posting.aboTopicId],
  )

  return (
    <>
      {generalPostingOptions.allowTemplateEdit &&
        <Paper className={classes.paperButtons} variant={generalPostingOptions.showAsPopUp ? undefined : 'elevation'}>
          <Grid container>
            <Grid item xs={12}>
              <Box className={classes.optionsContainer}>
                <Box className={classes.options}>
                  {postingOptions.length > 1 &&
                    <Box className={classes.innerOptions}>
                      <DropDownComponent
                        options={postingOptions}
                        value={postingType}
                        label={t('postingCommon:postingType')}
                        size="small"
                        disableClearable
                        onChange={(option) => {
                          if (option.key === PostingType.template) {
                            changeTopic(undefined)
                            dispatchPostingType(PostingType.template)
                          } else {
                            changeTopic(undefined)
                            dispatchPostingType(PostingType.posting)
                          }
                        }}
                      />
                    </Box>}
                  <Box>
                    {postingType === PostingType.template
                      ? (
                        <>
                          {categoryData.length > 0 &&
                            <DropDownComponent
                              options={categoryData}
                              label={t('postingCommon:category')}
                              size="small"
                              value={posting.aboTopicId ? posting.aboTopicId : [{
                                key: '',
                                value: ''
                              }] as DropDownElement[]}
                              placeholder={t('postingCommon:choose')}
                              onChange={(option: DropDownElement) => {
                                changeTopic(option)
                              }}
                            />}
                        </>
                      )
                      : (
                        <>
                          {topicData.length > 0 &&
                            <DropDownComponent
                              options={topicData}
                              label={t('postingCommon:aboTopic')}
                              size="small"
                              value={topicId || [{ key: '', value: '' }] as DropDownElement[]}
                              placeholder={t('postingCommon:choose')}
                              onChange={(option: DropDownElement) => {
                                changeTopic(option)
                                setTopicId(option.key)
                              }}
                            />}
                        </>)}
                  </Box>
                </Box>
                <Box className={classes.options}>
                  {generalPostingOptions.allowLinkIndividualisation && generalPostingOptions.showLinkIndividualisation &&
                    <Box className={classes.innerOptions}>
                      <FormControlLabel
                        control={
                          <SwitchComponent
                            checked={manipulateLinks}
                            color="primary"
                            id="newPostingManipulateLinks"
                            onChange={e => {
                              setPosting({
                                ...posting,
                                manipulateLinks: e.target.checked,
                              })
                              setManipulateLinks(e.target.checked)
                              props.changePosting && props.changePosting({
                                ...posting,
                                manipulateLinks: e.target.checked
                              })
                            }}
                          />
                        }
                        label={<>{t('postingCommon:manipulateLinks')}</>}
                        labelPlacement="start"
                      />
                    </Box>}
                  {generalPostingOptions.allowIndividualHashtags && generalPostingOptions.showIndividualHashtags &&
                    <Box>
                      <FormControlLabel
                        control={
                          <SwitchComponent
                            checked={addHashtag}
                            color="primary"
                            id="newPostingAddHashtag"
                            onChange={e => {
                              setPosting({
                                ...posting,
                                addHashtag: e.target.checked,
                              })
                              setAddHashtag(e.target.checked)
                              props.changePosting && props.changePosting({ ...posting, addHashtag: e.target.checked })
                            }}
                          />
                        }
                        label={<>{t('postingCommon:addHashtag')}</>}
                        labelPlacement="start"
                      />
                    </Box>}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>}
      <Grid container>
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="space-between" spacing={2}
            className={showAsPopUp ? '' : classes.options}>
            {socialMediaNetworkCount > 1 &&
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                flex: 'none',
              }}
              >
                <Typography
                  style={{ display: 'inline-flex' }}
                  variant="body1"
                >
                  {generalPostingOptions.allowTemplateEdit
                    ? `${t('postingCommon:provideFor')}`
                    : `${t('postingCommon:isAvailableAt')}`}
                </Typography>
              
                <FormControlLabel
                  control={
                    <SwitchComponent
                      disabled={!socialMediaNetworks?.facebook}
                      checked={facebook}
                      color="primary"
                      id="newPostingFacebook"
                      onChange={e => {
                        changeSocialMedia(e.target.checked, 'facebook')
                        setFacebook(e.target.checked)
                      }}
                    />
                  }
                  label={<Facebook color="info"/>}
                  labelPlacement="start"
                />
                <FormControlLabel
                  control={
                    <SwitchComponent
                      disabled={!socialMediaNetworks?.instagram}
                      checked={instagram}
                      color="primary"
                      id="newPostingInstagram"
                      onChange={e => {
                        changeSocialMedia(e.target.checked, 'instagram')
                        setInstagram(e.target.checked)
                      }}
                    />
                  }
                  label={<Instagram color="info"/>}
                  labelPlacement="start"
                />
                <FormControlLabel
                  control={
                    <SwitchComponent
                      disabled={!socialMediaNetworks?.googleMyBusiness}
                      checked={googleMyBusiness}
                      color="primary"
                      id="newPostingGoogleMyBusiness"
                      onChange={e => {
                        changeSocialMedia(e.target.checked, 'googleMyBusiness')
                        setGoogleMyBusiness(e.target.checked)
                      }}
                    />
                  }
                  label={<Google color="info"/>}
                  labelPlacement="start"
                />
                <FormControlLabel
                  control={
                    <SwitchComponent
                      disabled={!socialMediaNetworks?.linkedIn}
                      checked={linkedIn}
                      color="primary"
                      id="newPostinglinkedIn"
                      onChange={e => {
                        changeSocialMedia(e.target.checked, 'linkedIn')
                        setLinkedIn(e.target.checked)
                      }}
                    />
                  }
                  label={<LinkedIn color="info"/>}
                  labelPlacement="start"/>
              </Box>}
            <Grid container item xs={12} className={classes.dateOptions}>
              {show ? (
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={deLocale}>
                  <Grid item xs={6} sm={4}>
                    <Box sx={{ ml: 'auto', mr: 2, mt: 1 }}>
                      <DateTimePicker
                        disabled={!!props.posting.pagePostId}
                        label={`${generalPostingOptions.allowTemplateEdit ? t('postingCommon:postStart') : showAsPopUp ? t('postingCommon:postStart') : t('postingCommon:postAt')}`}
                        value={setMinDate(props.date.start ? new Date(props.date.start) : posting.postStart)}
                        minDateTime={new Date()}
                        onChange={val => {
                          if (val) {
                            !isNaN(val.getTime()) && handleChange(val, 'start')
                          } else {
                            handleChange(null, 'start')
                          }
                        }}
                        slotProps={{
                          textField: { size: 'small' }
                        }}
                        viewRenderers={{
                          hours: renderTimeViewClock,
                          minutes: renderTimeViewClock,
                          seconds: renderTimeViewClock,
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} sx={{ marginRight: showAsPopUp ? '40px' : '' }}>
                    <Box sx={{ mr: 2, mt: 1 }}>
                      <DateTimePicker
                        disabled={!!props.posting.pagePostId}
                        label={t('postingCommon:postEnd')}
                        value={props.date.end || null}
                        minDateTime={props.date.start || startOfToday()}
                        onChange={val => handleChange(val, 'end')}
                        slotProps={{
                          textField: { size: 'small' }
                        }}
                        viewRenderers={{
                          hours: renderTimeViewClock,
                          minutes: renderTimeViewClock,
                          seconds: renderTimeViewClock,
                        }}
                      />
                    </Box>
                  </Grid>
                </LocalizationProvider>
              ) : null}
              <TooltipComponent
                title={`${buttonTooltip}`}
                aria-label={`${buttonTooltip}`}
                child={
                  <ButtonGroup className={classes.buttonGroup} variant="contained" color="primary"
                    aria-label="contained button group">
                    <ButtonComponent
                      disabled={disablePostingButton}
                      text={setButtonText()}
                      onClick={() => handlePublishAction()}
                    />
                    <TooltipComponent
                      title={`${t('postingCommon:chooseDate')}`}
                      child={
                        <Button
                          disabled={disablePostingButton}
                          onClick={() => setShow(!show)}
                        >
                          <AccessTime/>
                        </Button>
                      }
                    />
                  </ButtonGroup>
                }
              />
            </Grid>
          </Stack>
        </Grid>
      </Grid>
      <DialogComponent
        type="error"
        title={t('postingCommon:noTopicsAvailableTitle')}
        message={t('postingCommon:noTopicsAvailable')}
        open={showTopicError}
        autoClose={false}
        confirmLabel={t('postingCommon:confirm')}
        cancelLabel={t('postingCommon:cancel')}
        requestActions
        onClose={() => setShowTopicError(false)}
        onCancel={() => setShowTopicError(false)}
        onConfirm={() => {
          setShowTopicError(false)
          const role: string = user ? user.role.toLowerCase() : 'editor'
          history.push(`/${role}/topics`)
        }}
      />
    </>
  )
}

export default CreatePostingOptions
